import React, { useEffect, useMemo, useState } from "react";
import { useApiTrigger } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { ContactVendorRequest } from "../../../../../../common/service/server/dto/UpdateVendorRequest";
import { Error as RequestsError } from "../../../../../copy/requests";
import { usePrimaryOrganization } from "../../../../../hooks";
import { useContactVendorsMessageTemplate } from "../../../../../hooks/useTemplates";
import { Routes } from "../../../../../root/routes";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { VendorEmailDisplayDialog } from "../../../../dialog/VendorEmailDisplayDialog";
import { EmailButton } from "../../../../organisms/EmailButton";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import {
  isRequestContextApplicable,
  isRequestContextApplicableWithCrunchFiltering,
  isRequestRegionApplicable,
  isVendorDeletabilityConfiguredForPartialDeletion,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { shouldNotContactForDelete } from "../../../../../util/requestInstructions";
import { Tooltip } from "@mui/material";
import { DoNotContactReason } from "../../../../../../common/service/server/dto/RequestHandlingInstructionDto";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { Organization } from "../../../../../../common/models";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { OrganizationManagedAdNetworkDto } from "../../../../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import { getVendorNesting, isConnectedSystemVendor } from "../../../../../util/vendorNesting";

/**
 * Check if a request outcome was due to a successful automation.
 * @param vendorOutcome The data recipient's outcome value.
 */
const isSuccessfulAutomatedOutcome = (vendorOutcome: string): boolean => {
  return ["AUTOMATION_REQUESTED", "AUTOMATION_NO_DATA", "AUTOMATION_CONFIRMED"].includes(
    vendorOutcome,
  );
};

/**
 * Returns the filtered list of vendors that are eligible for delete requests, and for vendors that are not eligible, a map of reasons why.
 * If no `request` is provided, no request-specific filters will be checked.
 */
export const eligibleVendorsToContactForDeleteRequest = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto | null, // if null, will skip request-specific filters
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  managedAdNetworks?: OrganizationManagedAdNetworkDto[],
  organization?: Organization,
): [OrganizationDataRecipientDto[], Map<string, string>] => {
  let vendorRemovalReasons = new Map<string, string>();

  const nesting = getVendorNesting(vendors, managedAdNetworks, sellingAndSharingAnswers);

  (vendors || []).forEach((v) => {
    // First do checks that are request-specific
    if (request) {
      if (
        v.isInstalled &&
        !(
          request.dataRequestType.regulationLaw.slug === "GDPR" &&
          v.gdprProcessorSetting === "Processor"
        )
      ) {
        vendorRemovalReasons.set(
          v.vendorId,
          `installed but does not meet GDPR processor requirement, current gdprProcesssorSetting is ${v.gdprProcessorSetting}`,
        );
        return;
      }

      const contextApplicable = organization
        ? isRequestContextApplicableWithCrunchFiltering(
            dataMap,
            request.context,
            v,
            request,
            organization,
          )
        : isRequestContextApplicable(dataMap, request.context, v);

      if (!contextApplicable) {
        vendorRemovalReasons.set(v.vendorId, "not applicable to this request context");
        return;
      }

      if (!isRequestRegionApplicable(request, v)) {
        vendorRemovalReasons.set(v.vendorId, "not applicable to this request region");
        return;
      }
    }

    // Always hide vendors that are nested under a manager (the manager handles them)
    if (nesting.isManaged(v.id)) {
      vendorRemovalReasons.set(v.vendorId, "nested vendor (handled by its managing vendor)");
      return;
    }

    // TrueVault talks to the Connected System through its automation, so it is never emailed (POL-7958).
    if (isConnectedSystemVendor(v)) {
      vendorRemovalReasons.set(v.vendorId, "handled by TrueVault Connected System automation");
      return;
    }

    // Hide internal storage locations (although note that they could be added back later - see POL-6994)
    if (v.dataRecipientType === "storage_location") {
      vendorRemovalReasons.set(v.vendorId, "internal storage location");
      return;
    }

    const instruction = v.instructions.find((vi) => vi.requestType == "DELETE");
    // Filter out vendors whose data deletability is "No - data must be retained"
    if (
      instruction?.processingMethod == "RETAIN" &&
      v.dataDeletability !== "NOT_DELETABLE" &&
      !instruction.hasRetentionExceptions
    ) {
      vendorRemovalReasons.set(
        v.vendorId,
        `DELETE instruction processingMethod is not RETAIN, is instead ${instruction?.processingMethod}, and vendor is not marked as unable to delete, is instead ${v.dataDeletability}`,
      );
      return;
    }
  });

  let filteredVendors = vendors.filter((v) => !vendorRemovalReasons.has(v.vendorId));

  // Add all management vendors that meet the criteria for delete requests
  if (nesting.managedVendorIds.length > 0) {
    const managementVendorsToAdd = nesting.activeManagers.filter((vendor) => {
      if (filteredVendors.find((v) => v.vendorId === vendor.vendorId)) return false; // Already included

      // Don't add if they would be filtered out by other criteria
      if (vendorRemovalReasons.has(vendor.vendorId)) return false;

      return true; // Include management vendors for delete requests
    });

    filteredVendors.push(...managementVendorsToAdd);
  }

  return [filteredVendors, vendorRemovalReasons];
};

export const vendorsMissingDoNotContactExplanationForDeleteRequest = (
  vendors: OrganizationDataRecipientDto[],
) => {
  return (vendors || []).filter((v) => {
    const instruction = v.instructions.find((vi) => vi.requestType == "DELETE");

    const thirdParty =
      v.classification.slug == "third-party" || v.dataRecipientType == "third_party_recipient";
    const doNotContact = instruction?.doNotContact;
    const impossible =
      instruction?.doNotContactReason == DoNotContactReason.IMPOSSIBLE_OR_DIFFICULT;
    const noExplanation = !instruction?.doNotContactExplanation;

    return thirdParty && doNotContact && impossible && noExplanation;
  });
};

/**
 * Returns the filtered list of vendors that should be contacted for delete requests, and for vendors that are not contacted, a map of reasons why.
 * If no `request` is provided, no request-specific filters will be checked.
 */
export const vendorsToContactForDeleteRequest = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto, // if null, will skip request-specific filters (i.e. may return some vendors that may not be contacted given a specific request)
): [OrganizationDataRecipientDto[], Map<string, string>] => {
  let vendorRemovalReasons = new Map<string, string>();

  (vendors || []).forEach((v) => {
    // First do checks that are request-specific
    if (request) {
      const vendorOutcome = request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId);
      const outcome = vendorOutcome?.outcome;
      const dataNotFound = outcome === "NO_DATA";
      const dataRetained = outcome === "DATA_RETAINED";

      if (dataNotFound) {
        vendorRemovalReasons.set(v.vendorId, `marked 'No data to delete' in Delete Info step`);
        return;
      }

      if (dataRetained && !isVendorDeletabilityConfiguredForPartialDeletion(v)) {
        vendorRemovalReasons.set(
          v.vendorId,
          `marked 'Data retained' in Delete Info step (and is NOT configured for partial deletion)`,
        );
        return;
      }
    }

    const instruction = v.instructions.find((vi) => vi.requestType == "DELETE");

    const doNotContactDefault = shouldNotContactForDelete(v, instruction);
    const noDeletionInstructions = !v.deletionInstructions || instruction?.doNotContact === false;
    const noDoNotContact = !doNotContactDefault && !instruction?.doNotContact;

    if (doNotContactDefault) {
      vendorRemovalReasons.set(v.vendorId, "default doNotContact is true based on vendor settings");
      return;
    }

    if (!noDeletionInstructions) {
      vendorRemovalReasons.set(
        v.vendorId,
        "no deletion instructions in vendor database or DELETE instruction doNotContact is true",
      );
      return;
    }

    if (!noDoNotContact) {
      vendorRemovalReasons.set(v.vendorId, "doNotContact is marked as true");
      return;
    }
  });

  return [vendors.filter((v) => !vendorRemovalReasons.has(v.vendorId)), vendorRemovalReasons];
};

export const ContactVendors = () => {
  const [org] = usePrimaryOrganization();
  const {
    request,
    refreshRequest,
    setStepCompleted,
    vendors: { all: vendors },
  } = useDataRequestProcessingContext();
  const { data: dataMap } = useDataMap(org.id);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);
  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);

  const showDiagnostics = new URLSearchParams(window.location.search).get("dg") == "1";

  const [template] = useContactVendorsMessageTemplate(org.id, request.id);

  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [removedRecipients, setRemovedRecipients] = useState(new Set<string>());

  const closeModal = () => setNotificationModalOpen(false);

  const [updateVendorOutcome, updateVendorOutcomeRequest] = useApiTrigger(
    (values: ContactVendorRequest) => Api.dataRequest.contactVendors(org.id, request.id, values),
    refreshRequest,
    RequestsError.TransitionProcessing,
  );

  const [filteredVendors, filteredVendorsRemovalReasons] = eligibleVendorsToContactForDeleteRequest(
    vendors,
    request,
    dataMap,
    answers,
    managedAdNetworks,
    org,
  );

  const missingDoNotContactExplanation = sortVendorsByCategory(
    vendorsMissingDoNotContactExplanationForDeleteRequest(filteredVendors),
  );

  let [contactVendorsList, contactVendorsRemovalReasons] = vendorsToContactForDeleteRequest(
    filteredVendors,
    request,
  );

  // With POL-6994 we allow adding internal storage locations to the contact step, which is indicated by a storage location having `doNotContact = false`
  vendors
    .filter((v) => v.dataRecipientType === "storage_location")
    .forEach((vendor) => {
      const instruction = vendor.instructions.find((i) => i.requestType === "DELETE");
      if (
        instruction &&
        instruction.doNotContact === false &&
        !contactVendorsList.some((v) => v.vendorId === vendor.vendorId)
      ) {
        contactVendorsList.push(vendor);
        if (contactVendorsRemovalReasons.has(vendor.vendorId)) {
          contactVendorsRemovalReasons.delete(vendor.vendorId);
        }
      }
    });

  contactVendorsList = sortVendorsByCategory(contactVendorsList);

  // Nested vendors are handled by their manager, so a missing contact email on one is not actionable here.
  const { isManaged, getManagedVendorsForManager } = getVendorNesting(
    vendors,
    managedAdNetworks,
    answers,
  );

  const noSendVendorList =
    contactVendorsList?.filter((v) => !v.email && !v.defaultEmail && !isManaged(v.id)) || [];

  const sendVendorList = useMemo(
    () =>
      contactVendorsList?.filter(
        (v) =>
          (v.email || v.defaultEmail) &&
          (request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId && !x.processed) ||
            request.vendorOutcomes.every((x) => x.vendor.id !== v.vendorId)),
      ),
    [request, contactVendorsList],
  );

  // Existing behavior was that once a user has emailed vendors (even a subset of them), they can not send another email
  // for the request. This was fine before automation started marking vendors as automatically processed. Now that there
  // could be already-processed vendors by this step, filter automated vendors from the list of completed vendors, since
  // they'll be marked as processed already, but we still want to be able to send at least one email for other vendors.
  const completedVendorList =
    contactVendorsList.filter(
      (v) =>
        (v.email || v.defaultEmail) &&
        request.vendorOutcomes.find(
          (x) =>
            x.vendor.id === v.vendorId && x.processed && !isSuccessfulAutomatedOutcome(x.outcome),
        ),
    ) || [];

  const vendorsContacted = completedVendorList?.length > 0;

  const hasValidRecipients = sendVendorList.some((v) => !removedRecipients.has(v.vendorId));

  useEffect(() => {
    setStepCompleted(
      !hasValidRecipients ||
        (vendorsContacted &&
          noSendVendorList.length === 0 &&
          missingDoNotContactExplanation.length === 0),
    );
  }, [
    vendorsContacted,
    noSendVendorList.length,
    missingDoNotContactExplanation.length,
    hasValidRecipients,
  ]);

  if (!vendors) return null;

  const dispatchEmail = () => {
    const values = {
      vendors: sendVendorList.map((v) => v.vendorId).filter((id) => !removedRecipients.has(id)),
    };
    updateVendorOutcome(values);
  };

  return (
    <>
      {!hasValidRecipients && (
        <p>No vendors need to be contacted for this request. You can proceed to the next step.</p>
      )}
      {request.contactVendorsEverHidden && (
        <Callout
          collapsible
          className="mb-xxl"
          title={<span className="text-t2">Why was the 'contact vendors' step added?</span>}
          variant={CalloutVariant.Clear}
        >
          'Contact vendors' is required based on your selections in the 'delete info' step.
        </Callout>
      )}

      {hasValidRecipients && (
        <div className="text-t3 mb-std">
          To fulfill your data privacy obligation, the message below will be emailed to any vendors
          that do not have official deletion instructions that were linked to in the previous step.
        </div>
      )}

      {completedVendorList.length > 0 && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            <p>
              Message successfully sent to{" "}
              <strong>{completedVendorList.map((v) => v.name).join(", ")}.</strong>
            </p>
          </Callout>
        </div>
      )}

      {sendVendorList.length > 0 && (
        <>
          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To:</strong>{" "}
                {vendorsContacted ? (
                  <Tooltip title="You have already contacted vendors for this request">
                    <span className="color-neutral-600 text-decoration-underline">
                      See Recipients
                    </span>
                  </Tooltip>
                ) : (
                  <span className="tw-boundary">
                    <span
                      className="text-blue-800 underline cursor-pointer"
                      onClick={() => setNotificationModalOpen(true)}
                    >
                      See Recipients
                    </span>
                  </span>
                )}
              </p>
              <p>
                <strong>
                  Subject: {request.dataRequestType.regulationLaw.abbreviation}{" "}
                  {request.dataRequestType.label}
                </strong>
              </p>

              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>
        </>
      )}

      {noSendVendorList.length > 0 &&
        (noSendVendorList.length != contactVendorsList.length ? (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent to the following Data Recipients because there is no
                email address added for them:{" "}
                <strong>{noSendVendorList.map((v) => v.name).join(", ")}.</strong>
              </p>
              <p>
                {" "}
                Visit{" "}
                <a href={Routes.organization.requestHandling.requestToDelete}>
                  Manage Request to Delete Settings
                </a>{" "}
                to add a contact email.
              </p>
            </Callout>
          </div>
        ) : (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent because none of your Data Recipients can be
                contacted. Add email addresses for the following Data Recipients in{" "}
                <a href={Routes.organization.requestHandling.requestToDelete}>
                  Manage Request to Delete Settings
                </a>
                , then return to this step to preview and send the notification:{" "}
                <strong>{noSendVendorList.map((v) => v.name).join(", ")}.</strong>
              </p>
            </Callout>
          </div>
        ))}

      {missingDoNotContactExplanation?.length > 0 && (
        <div className="w-896 my-md">
          <Callout variant={CalloutVariant.Yellow}>
            <p>
              This step cannot be completed because the following Third Parties are marked as “Do
              Not Contact” but lack required information:{" "}
              <strong>{missingDoNotContactExplanation.map((v) => v.name).join(", ")}</strong>.
            </p>
            <p>
              Visit{" "}
              <a href={Routes.organization.requestHandling.requestToDelete}>
                Manage Request to Delete Settings
              </a>{" "}
              to add the missing information or update contact settings.
            </p>
          </Callout>
        </div>
      )}

      {(sendVendorList.length > 0 || noSendVendorList.length > 0) && (
        <EmailButton
          color="primary"
          variant="contained"
          className="btn-md mb-std"
          loading={updateVendorOutcomeRequest.running}
          onClick={dispatchEmail}
          disabled={noSendVendorList?.length > 0 || !hasValidRecipients || vendorsContacted}
          disabledTooltip={
            vendorsContacted
              ? "You have already contacted vendors for this request"
              : hasValidRecipients
              ? noSendVendorList?.length > 0
                ? "There are data recipients with no email address associated"
                : undefined
              : "You must contact at least one vendor"
          }
        >
          Send
        </EmailButton>
      )}

      {showDiagnostics && (
        <div className="tw-boundary">
          <div className="mt-[48px] text-neutral-900 text-[13px]">
            <hr className="mb-3" />
            {Array.from(filteredVendorsRemovalReasons.entries()).map(([vendorId, reason]) => {
              let vendor = vendors.find((v) => v.vendorId == vendorId);
              if (!vendor) {
                return null;
              }
              return (
                <div key={vendorId} className="mb-1">
                  {vendor.name} removed because: {reason}
                </div>
              );
            })}

            <br />
            <br />

            {Array.from(contactVendorsRemovalReasons.entries()).map(([vendorId, reason]) => {
              let vendor = vendors.find((v) => v.vendorId == vendorId);
              if (!vendor) {
                return null;
              }

              // Show which vendors are nested under this vendor
              const networksManaged = getManagedVendorsForManager(vendor.id);

              const managedNetworksText =
                networksManaged.length > 0
                  ? ` (manages: ${networksManaged.map((n) => n.name).join(", ")})`
                  : "";

              return (
                <div key={vendorId} className="mb-1">
                  {vendor.name}
                  {managedNetworksText} is not contacted because: {reason}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <VendorEmailDisplayDialog
        open={notificationModalOpen}
        onClose={closeModal}
        vendors={sendVendorList}
        allowToggle={true}
        isRecipientRemoved={(v) => removedRecipients.has(v.vendorId)}
        removeRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients, v.vendorId]));
        }}
        addRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients].filter((id) => id !== v.vendorId)));
        }}
      />
    </>
  );
};
